<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  canViewAllContacts: {
    type: Boolean,
    default: false,
  },
  priorityTeamId: {
    type: Number,
    default: null,
  },
  teamIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });
const canViewAllContacts = ref(props.canViewAllContacts);
const priorityTeamId = ref(props.priorityTeamId);
const currentUserId = computed(() => store.getters.getCurrentUserID);

const isPriorityTeamEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.AUTO_ASSIGN_TEAM_FROM_ASSIGNEE)
);

const allTeams = useMapGetter('teams/getTeams');
const agentTeams = computed(() =>
  allTeams.value.filter(team => props.teamIds.includes(team.id))
);

onMounted(() => {
  if (isPriorityTeamEnabled.value && !allTeams.value.length) {
    store.dispatch('teams/get');
  }
});

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const selectedCustomRole = computed(() =>
  getCustomRoles.value.find(role => role.id === selectedRole.value?.id)
);

const customRoleGrantsAllContacts = computed(
  () =>
    selectedRole.value?.name?.startsWith('custom_') &&
    selectedCustomRole.value?.permissions?.includes('contact_manage')
);

watch(customRoleGrantsAllContacts, hasContactManage => {
  if (hasContactManage) {
    canViewAllContacts.value = false;
  }
});

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
      can_view_all_contacts: customRoleGrantsAllContacts.value
        ? false
        : canViewAllContacts.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    if (isPriorityTeamEnabled.value) {
      payload.priority_team_id = priorityTeamId.value || null;
    }

    await store.dispatch('agents/update', payload);
    if (currentUserId.value === props.id) {
      await store.dispatch('validityCheck');
    }
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div v-if="isPriorityTeamEnabled" class="w-full">
        <label>
          {{ $t('AGENT_MGMT.EDIT.FORM.PRIORITY_TEAM.LABEL') }}
          <select v-model="priorityTeamId" :disabled="!agentTeams.length">
            <option :value="null">
              {{ $t('AGENT_MGMT.EDIT.FORM.PRIORITY_TEAM.NONE') }}
            </option>
            <option v-for="team in agentTeams" :key="team.id" :value="team.id">
              {{ team.name }}
            </option>
          </select>
          <span class="text-sm leading-5 text-n-slate-11">
            {{
              agentTeams.length
                ? $t('AGENT_MGMT.EDIT.FORM.PRIORITY_TEAM.HELP_TEXT')
                : $t('AGENT_MGMT.EDIT.FORM.PRIORITY_TEAM.NO_TEAMS_HELP')
            }}
          </span>
        </label>
      </div>

      <div class="flex gap-3 items-start w-full mt-2">
        <Checkbox
          v-model="canViewAllContacts"
          class="mt-1 shrink-0"
          :disabled="customRoleGrantsAllContacts"
        />
        <div class="flex flex-col gap-1 min-w-0 flex-1">
          <span class="text-sm font-medium leading-6 text-n-slate-12">
            {{ $t('AGENT_MGMT.EDIT.FORM.CAN_VIEW_ALL_CONTACTS.LABEL') }}
          </span>
          <p class="text-sm leading-5 text-n-slate-11 m-0">
            {{ $t('AGENT_MGMT.EDIT.FORM.CAN_VIEW_ALL_CONTACTS.HELP_TEXT') }}
          </p>
        </div>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
